<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Table from 'dashboard/components-next/table/Table.vue';
import TableEmpty from 'dashboard/components-next/table/TableEmpty.vue';
import { stageColor } from 'dashboard/components-next/Contacts/contactStageColors';
import { syncedListAttributes } from 'dashboard/components-next/Contacts/syncedAttributes';
import { relativeTime as formatRelativeTime } from 'dashboard/components-next/Contacts/contactTime';

// Table view of the contacts list (replaces the stacked cards), matching the
// CrmB mockup: checkbox · contacto · una columna por atributo sincronizado
// con etiquetas · teléfono · correo · último mensaje. Selection and "open
// contact" behave exactly like the old ContactsList.vue (same props/emits),
// so ContactsIndex only swaps the component.
const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['toggleContact', 'toggleAll']);

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const contactAttributes = useMapGetter('attributes/getContactAttributes');
// Las dos primeras caben cómodas; con más, el ancho se lo reparten entre
// ellas para no dejar sin sitio al teléfono y al correo.
const chipAttributes = computed(() =>
  syncedListAttributes(contactAttributes.value).slice(0, 2)
);

const columns = computed(() => [
  {
    key: 'contact',
    label: t('CONTACTS_LAYOUT.HEADER.TABLE.CONTACT'),
    width: 'w-[24%]',
  },
  ...chipAttributes.value.map(attribute => ({
    key: `attribute-${attribute.attributeKey}`,
    label: attribute.attributeDisplayName,
    width: 'w-[13%]',
    attribute,
  })),
  {
    key: 'phone',
    label: t('CONTACTS_LAYOUT.HEADER.TABLE.PHONE'),
    width: 'w-[15%]',
  },
  { key: 'email', label: t('CONTACTS_LAYOUT.HEADER.TABLE.EMAIL') },
  {
    key: 'lastMessage',
    label: t('CONTACTS_LAYOUT.HEADER.TABLE.LAST_MESSAGE'),
    width: 'w-[13%]',
  },
]);

// The contacts getter camel-cases nested keys, so `tipo_contacto` arrives as
// `tipoContacto`; definitions keep the snake_case key. Try both.
const camelize = key =>
  String(key).replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
const attributeValue = (contact, attribute) => {
  if (!attribute) return null;
  const attrs = contact.customAttributes || contact.custom_attributes || {};
  const key = attribute.attributeKey;
  return attrs[key] ?? attrs[camelize(key)] ?? null;
};

const chipStyle = value => {
  const color = stageColor(value);
  return { backgroundColor: color.bg, color: color.text };
};
const dotStyle = value => ({ backgroundColor: stageColor(value).dot });

// "hace 4 min" / "4 min ago" in the UI language, plus a dot that cools down
// with age: green < 1 h, blue < 1 d, amber < 7 d, grey after.
const relativeTime = timestamp => formatRelativeTime(timestamp, locale.value);
const activityDot = timestamp => {
  if (!timestamp) return '#c3ced7';
  const age = Date.now() / 1000 - timestamp;
  if (age < 3600) return '#2e8462';
  if (age < 86400) return '#3b7fc4';
  if (age < 86400 * 7) return '#b58218';
  return '#93a2ae';
};

const openContact = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };
  await router.push({ name, params, query: route.query });
};

const selectedSet = computed(() => new Set(props.selectedContactIds || []));
const rowClass = contact =>
  selectedSet.value.has(contact.id) ? 'bg-n-alpha-1' : '';
</script>

<template>
  <Table
    :columns="columns"
    :rows="contacts"
    :selected-ids="selectedContactIds"
    :row-class="rowClass"
    selectable
    clickable
    @row-click="contact => openContact(contact.id)"
    @toggle-row="payload => emit('toggleContact', payload)"
    @toggle-all="value => emit('toggleAll', value)"
  >
    <template #cell-contact="{ row }">
      <div class="flex items-center gap-2.5 min-w-0">
        <Avatar
          :name="row.name || '?'"
          :src="row.thumbnail"
          :size="28"
          rounded-full
        />
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ row.name || t('CONTACTS_LAYOUT.HEADER.TABLE.UNNAMED') }}
          </span>
          <span
            v-if="row.company?.name"
            class="text-xs text-n-slate-10 truncate"
          >
            {{ row.company.name }}
          </span>
        </div>
      </div>
    </template>

    <template
      v-for="attribute in chipAttributes"
      :key="attribute.attributeKey"
      #[`cell-attribute-${attribute.attributeKey}`]="{ row }"
    >
      <span
        v-if="attributeValue(row, attribute)"
        class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium max-w-full"
        :style="chipStyle(attributeValue(row, attribute))"
      >
        <span
          class="size-1.5 rounded-full flex-shrink-0"
          :style="dotStyle(attributeValue(row, attribute))"
        />
        <span class="truncate">{{ attributeValue(row, attribute) }}</span>
      </span>
      <TableEmpty v-else />
    </template>

    <template #cell-phone="{ row }">
      <span
        v-if="row.phoneNumber"
        class="block text-sm text-n-slate-12 truncate"
      >
        {{ row.phoneNumber }}
      </span>
      <TableEmpty v-else />
    </template>

    <template #cell-email="{ row }">
      <span v-if="row.email" class="block text-sm text-n-blue-text truncate">
        {{ row.email }}
      </span>
      <TableEmpty v-else />
    </template>

    <template #cell-lastMessage="{ row }">
      <span
        v-if="row.lastActivityAt"
        class="inline-flex items-center gap-1.5 text-xs text-n-slate-11 truncate"
      >
        <span
          class="size-1.5 rounded-full flex-shrink-0"
          :style="{ backgroundColor: activityDot(row.lastActivityAt) }"
        />
        {{ relativeTime(row.lastActivityAt) }}
      </span>
      <TableEmpty v-else />
    </template>
  </Table>
</template>
